'use client';

import { Suspense, startTransition, createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { LEARNER_ACCESS_ENDED_EVENT } from '@/lib/http/learner-fetch';
import { Button, buttonVariants } from '@/components/ui/button';
import { LearnerAccessPendingContext, LearnerAccessPlaceholder } from '@/components/course/learner-access-pending';

const CallActivity = createContext<(active: boolean) => void>(() => {});

/** Keep an already-running call mounted until its existing lifecycle ends. */
export function useLearnerCallActivity(active: boolean) {
  const report = useContext(CallActivity);
  useEffect(() => {
    if (!active) return;
    report(true);
    return () => report(false);
  }, [active, report]);
}

function safeSalesUrl(value?: string) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch { return null; }
}

const SESSION_PATH = /^\/practice\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;
const LMS_PATH = /^\/lms(?:\/|$)/;

type Verdict = 'active' | 'access_ended' | 'unavailable';

async function readAccess(pathname: string): Promise<Verdict> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const sessionId = pathname.match(SESSION_PATH)?.[1] ?? null;
    const { data, error } = await Promise.race([
      createClient().rpc('owned_learner_access_state', { p_session_id: sessionId, p_course_id: null }),
      new Promise<never>((_, reject) => { timeout = setTimeout(() => reject(new Error('timeout')), 10_000); }),
    ]);
    if (!error) {
      if (data === 'access_ended') return 'access_ended';
      if (['active', 'not_enrolled', 'not_found'].includes(data)) return 'active';
    }
  } catch { /* Outages never imply ended access. */ }
  finally { clearTimeout(timeout); }
  return 'unavailable';
}

/**
 * The only search-params reader in the boundary (#2830). Next.js requires that
 * read to sit under a Suspense boundary, so it lives here with a null fallback
 * instead of wrapping the whole application. It re-verifies on every path or
 * query change and returns the parent's cleanup so a stale check is discarded.
 */
function NavigationObserver({ actorId, pathname, onNavigate }: {
  actorId: string | null;
  pathname: string;
  onNavigate: () => () => void;
}) {
  const search = useSearchParams()?.toString() ?? '';
  useEffect(onNavigate, [actorId, pathname, search, onNavigate]);
  return null;
}

export function LearnerAccessBoundary({ children, actorId, salesUrl }: {
  children: ReactNode;
  actorId: string | null;
  salesUrl?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const lmsPath = LMS_PATH.test(pathname);
  const protectedPath = lmsPath || /^\/practice(?:\/|$)/.test(pathname);
  const verifyNavigation = lmsPath || SESSION_PATH.test(pathname);
  // The verdict is scoped to the actor, not the page: a learner who was active
  // on the last page stays on screen while the next page is re-verified, and a
  // denial or resolved check always replaces the carried value.
  const [read, setRead] = useState<{ actorId: string; verdict: Verdict } | null>(null);
  const [checking, setChecking] = useState(false);
  const [activeCalls, setActiveCalls] = useState(0);
  const generation = useRef(0);
  const verifiedAfter = useRef(0);
  const invalidate = useCallback(() => { ++generation.current; }, []);
  const reportCall = useCallback((active: boolean) => setActiveCalls(count => Math.max(0, count + (active ? 1 : -1))), []);
  const check = useCallback(async () => {
    if (!actorId) return;
    const request = ++generation.current;
    verifiedAfter.current = performance.now();
    const verdict = await readAccess(pathname);
    if (request !== generation.current) return;
    setRead(previous => ({ actorId, verdict: verdict === 'unavailable' && previous?.actorId === actorId && previous.verdict === 'access_ended' ? 'access_ended' : verdict }));
    setChecking(false);
    return verdict;
  }, [actorId, pathname]);

  const onNavigate = useCallback(() => {
    if (actorId && protectedPath) {
      verifiedAfter.current = performance.now();
      if (verifyNavigation) startTransition(() => { void check(); });
    }
    return invalidate;
  }, [actorId, protectedPath, verifyNavigation, check, invalidate]);

  useEffect(() => {
    if (!actorId || !protectedPath) return;
    const denied = (event: Event) => {
      // A response from an abandoned page cannot override a newer access read.
      if ((event as CustomEvent<{ startedAt: number }>).detail.startedAt < verifiedAfter.current) return;
      invalidate();
      setChecking(false);
      setRead({ actorId, verdict: 'access_ended' });
    };
    window.addEventListener(LEARNER_ACCESS_ENDED_EVENT, denied);
    const onPageShow = (event: PageTransitionEvent) => { if (event.persisted && verifyNavigation) void check(); };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener(LEARNER_ACCESS_ENDED_EVENT, denied);
    };
  }, [actorId, protectedPath, check, invalidate, verifyNavigation]);

  const retry = async () => {
    setChecking(true);
    if (await check() === 'active') router.refresh();
  };
  const verdict = read?.actorId === actorId ? read.verdict : null;
  const gated = Boolean(actorId) && protectedPath && activeCalls === 0;
  const pending = gated && verifyNavigation && verdict === null;
  let content = children;
  // A confirmed denial follows the learner everywhere. An outage only gates the
  // routes that recheck on navigation; the practice launcher never does, so a
  // carried outage there would have no automatic way out.
  if (gated && (verdict === 'access_ended' || (verdict === 'unavailable' && verifyNavigation))) {
    content = <AccessEndedScreen ended={verdict === 'access_ended'} salesUrl={salesUrl} checking={checking} onRetry={retry} />;
  } else if (pending && !lmsPath) {
    // Practice deep links have no shell to keep mounted, and mounting the page
    // would start its launch requests before the verdict lands.
    content = <LearnerAccessPlaceholder />;
  }
  return (
    <CallActivity.Provider value={reportCall}>
      <LearnerAccessPendingContext.Provider value={pending}>
        <Suspense fallback={null}>
          <NavigationObserver actorId={actorId} pathname={pathname} onNavigate={onNavigate} />
        </Suspense>
        {content}
      </LearnerAccessPendingContext.Provider>
    </CallActivity.Provider>
  );
}

export function AccessEndedScreen({ ended = true, salesUrl, checking = false, onRetry }: {
  ended?: boolean;
  salesUrl?: string;
  checking?: boolean;
  onRetry: () => void;
}) {
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [ended]);
  const sales = safeSalesUrl(salesUrl);
  async function signOut() {
    setLoggingOut(true);
    setLogoutError(null);
    try {
      const response = await fetch('/api/auth/logout', { method: 'POST' });
      if (!response.ok) throw new Error('logout_failed');
      window.location.assign('/login');
    } catch {
      setLogoutError("Couldn't sign out. Please try again.");
      setLoggingOut(false);
    }
  }
  return (
    <main id="main" className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <section aria-labelledby="access-heading" className="w-full max-w-lg">
        <p className="mb-5 font-condensed text-sm font-bold uppercase tracking-widest text-muted-foreground">Account access</p>
        <h1 ref={heading} tabIndex={-1} id="access-heading" className="text-3xl font-semibold leading-tight tracking-tight outline-none sm:text-4xl">
          {ended ? 'Your account no longer has access.' : 'Unable to verify access'}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {ended ? 'Please contact sales to restore access.' : 'We could not check your access. Please try again.'}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {ended && sales ? <a className={buttonVariants({ size: 'xl' })} href={sales}>Contact sales</a> : null}
          <Button size="xl" variant="outline" disabled={checking} onClick={onRetry}>{checking ? 'Checking...' : 'Check access'}</Button>
          <Button size="xl" variant="ghost" disabled={loggingOut} onClick={signOut}>{loggingOut ? 'Signing out...' : 'Sign out'}</Button>
        </div>
        {logoutError ? <p role="alert" className="mt-4 text-sm text-destructive">{logoutError}</p> : null}
      </section>
    </main>
  );
}
